import { Button, MenuButton, Table, Tooltip } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { TermsOfUseDialog } from "./modals";
import { deserializeBankingToken, listBankingTokens } from "./api";
import { TokenRevokeDialog } from "./tokenWriteFormModals";
import TokenStateFilter from "./TokenStateFilter";
import SudoWritePane from "./SudoWritePane";
import useSudoWrite from "./useSudoWrite";
import {
  BankingTokenRecord,
  DeserializedBankingToken,
  TableColumn,
  TokenState,
} from "./types";
import { useCurrentUser } from "../../contexts/CurrentUserContext";

const LIST_ROUTE = "/api_banking_token_list";

const getTokenState = (token: DeserializedBankingToken): TokenState =>
  token.isExpired ? "inactive" : "active";

const BusinessBankingAPITokenListContent = () => {
  const { currentUser } = useCurrentUser();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const [tokenList, setTokenList] = useState<DeserializedBankingToken[]>([]);
  // Revoking a live token is a sudo-guarded write, so route it through the MFA
  // modal when the API requires re-verification.
  const runWrite = useSudoWrite(LIST_ROUTE);

  const [tosModalOpen, setTosModalOpen] = useState(false);

  // Per design, the list defaults to active tokens only; clearing the filter
  // (empty selection) shows every token regardless of state.
  const [selectedStates, setSelectedStates] = useState<Set<TokenState>>(
    new Set<TokenState>(["active"]),
  );

  const [tokenForRevoke, setTokenForRevoke] =
    useState<DeserializedBankingToken | null>(null);

  const visibleTokens =
    selectedStates.size === 0
      ? tokenList
      : tokenList.filter((token) => selectedStates.has(getTokenState(token)));

  const tableColumns: TableColumn[] = [
    { header: l10n.getString("table-header-nickname"), fieldName: "nickname" },
    {
      header: l10n.getString("table-header-created-by"),
      fieldName: "createdBy",
    },
    { header: l10n.getString("table-header-created"), fieldName: "created" },
    {
      header: l10n.getString("table-header-expires"),
      fieldName: "expires",
      cellFloatRightCallback: (row: DeserializedBankingToken) =>
        row.isExpired ? (
          <Tooltip text={l10n.getString("tooltip-token-expired")}>
            <span
              className="narmi-icon-info"
              style={{ color: "var(--color-errorDark)" }}
              role="img"
              aria-label={l10n.getString("tooltip-token-expired")}
            ></span>
          </Tooltip>
        ) : null,
    },
    { header: l10n.getString("table-header-last-used"), fieldName: "lastUsed" },
    {
      header: l10n.getString("table-header-scopes"),
      fieldName: "scopesDisplay",
    },
    {
      header: l10n.getString("table-header-allowlisted-ips"),
      fieldName: "ipAllowListDisplay",
    },
  ];

  useEffect(() => {
    const fetchTokens = async () => {
      const response = await listBankingTokens();
      setTokenList(response.results.map(deserializeBankingToken));
    };

    fetchTokens();
  }, []);

  const tosModalOnConfirm = () => {
    setTosModalOpen(false);
    navigate(`${LIST_ROUTE}/create`);
  };

  // Write access maps to the `change_bankingaccesstoken` permission: org admins
  // and the token's creator, while the token is still active. Read access
  // (`view_bankingaccesstoken`) is implied for any token shown in this list.
  const canWriteToken = (token: DeserializedBankingToken) =>
    Boolean(
      currentUser &&
        (currentUser.org_role === "Admin" ||
          token.createdBy === currentUser.username) &&
        !token.isExpired,
    );

  const setTokenState = (tokenState: BankingTokenRecord) => {
    const tokenListState = tokenList.map((token) =>
      token.uuid === tokenState.uuid
        ? deserializeBankingToken(tokenState)
        : token,
    );

    setTokenList(tokenListState);
  };

  const tokenDeleteModalOnConfirm = (
    revokedTokenResponse: BankingTokenRecord,
  ) => {
    setTokenState(revokedTokenResponse);
    setTokenForRevoke(null);
    sendNotification({
      type: "success",
      text: l10n.getString("notification-token-revoked"),
    });
  };

  return (
    <>
      {/* Begin Modals */}

      <TermsOfUseDialog
        isOpen={tosModalOpen}
        onUserDismiss={() => setTosModalOpen(false)}
        onConfirm={tosModalOnConfirm}
      />
      <TokenRevokeDialog
        isOpen={Boolean(tokenForRevoke)}
        onUserDismiss={() => setTokenForRevoke(null)}
        onConfirm={tokenDeleteModalOnConfirm}
        tokenForRevoke={tokenForRevoke}
        runWrite={runWrite}
      />

      {/* End Modals */}

      <div
        className="padding--top--l"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          height: "40px",
        }}
      >
        <TokenStateFilter
          selectedStates={selectedStates}
          onChange={setSelectedStates}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Button
            kind="tertiary"
            startIcon="external-link"
            onClick={() =>
              window.open("/v1/banking/docs/", "_blank", "noopener,noreferrer")
            }
            label={l10n.getString("link-text-api-documentation")}
          />
          <Button
            onClick={() => setTosModalOpen(true)}
            label={l10n.getString("button-new-api-token")}
          />
        </div>
      </div>
      {/* Begin Data Table */}
      <div className="padding--top--xl">
        <Table
          rowDensity="compact"
          colVisibility={["*", "m", "l", "l", "l", "m", "l", "*"]}
          colLayout={{
            s: "1fr min-content min-content min-content min-content min-content min-content min-content",
            m: "1.4fr 1fr min-content min-content min-content 1.2fr min-content min-content",
            l: "1.4fr 1fr 1fr 1fr 1fr 1.2fr 1.2fr min-content",
          }}
        >
          <Table.Header>
            {tableColumns.map((col) => (
              <Table.HeaderCell key={col.fieldName}>
                {col.header}
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell textAlign="end">
              <span
                style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                {l10n.getString("aria-label-token-table")}
              </span>
            </Table.HeaderCell>
          </Table.Header>
          <Table.Body>
            {visibleTokens.map((row) => (
              <Table.Row key={row.uuid}>
                {tableColumns.map((col) => (
                  <Table.Cell key={col.fieldName}>
                    {col.fieldName === "nickname" ? (
                      <span
                        title={String(row.nickname)}
                        style={{
                          display: "block",
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.nickname}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {row[col.fieldName as keyof typeof row]}
                        {col.cellFloatRightCallback &&
                          col.cellFloatRightCallback(row)}
                      </span>
                    )}
                  </Table.Cell>
                ))}
                <Table.Cell textAlign="end">
                  <MenuButton>
                    {/*
                      Write access mirrors the `change_bankingaccesstoken`
                      permission (org admin or the token's creator, and the
                      token is unexpired). Every token surfaced in this list is
                      readable, i.e. the user holds `view_bankingaccesstoken`,
                      so "View" is offered only to users who cannot change the
                      token, and "Edit" to those who can.
                    */}
                    {!canWriteToken(row) && (
                      <MenuButton.Item
                        label={l10n.getString("menu-item-view")}
                        onSelect={() =>
                          navigate(`${LIST_ROUTE}/${row.uuid}/view`)
                        }
                        startIcon="eye"
                      />
                    )}
                    {canWriteToken(row) && (
                      <MenuButton.Item
                        label={l10n.getString("menu-item-edit")}
                        onSelect={() =>
                          navigate(`${LIST_ROUTE}/${row.uuid}/edit`)
                        }
                        startIcon="edit-2"
                      />
                    )}
                    {canWriteToken(row) && (
                      <MenuButton.Item
                        label={l10n.getString("menu-item-delete")}
                        onSelect={() => setTokenForRevoke(row)}
                        startIcon="trash"
                      />
                    )}
                  </MenuButton>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      {/* End Data Table */}
    </>
  );
};

const BusinessBankingAPITokenList = () => (
  <SudoWritePane basename="api_banking_token_list">
    <BusinessBankingAPITokenListContent />
  </SudoWritePane>
);

export default BusinessBankingAPITokenList;
